import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const TalentAvailability = ({ onAnalyze }) => {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [location, setLocation] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [showResults, setShowResults] = useState(false);

  const skillCategories = [
    {
      category: 'Software Development',
      skills: [
        { value: 'javascript', label: 'JavaScript', demand: 'High', availability: 85 },
        { value: 'python', label: 'Python', demand: 'Very High', availability: 78 },
        { value: 'java', label: 'Java', demand: 'High', availability: 82 },
        { value: 'react', label: 'React.js', demand: 'Very High', availability: 72 },
        { value: 'nodejs', label: 'Node.js', demand: 'High', availability: 68 },
        { value: 'angular', label: 'Angular', demand: 'Medium', availability: 65 },
        { value: 'dotnet', label: '.NET', demand: 'Medium', availability: 70 },
        { value: 'php', label: 'PHP', demand: 'Medium', availability: 75 }
      ]
    },
    {
      category: 'Data & Analytics',
      skills: [
        { value: 'data-science', label: 'Data Science', demand: 'Very High', availability: 45 },
        { value: 'machine-learning', label: 'Machine Learning', demand: 'Very High', availability: 42 },
        { value: 'sql', label: 'SQL', demand: 'High', availability: 88 },
        { value: 'tableau', label: 'Tableau', demand: 'High', availability: 55 },
        { value: 'power-bi', label: 'Power BI', demand: 'High', availability: 62 },
        { value: 'r-programming', label: 'R Programming', demand: 'Medium', availability: 38 },
        { value: 'spark', label: 'Apache Spark', demand: 'Medium', availability: 35 }
      ]
    },
    {
      category: 'Cloud & DevOps',
      skills: [
        { value: 'aws', label: 'AWS', demand: 'Very High', availability: 52 },
        { value: 'azure', label: 'Global Technology Leader Azure', demand: 'High', availability: 48 },
        { value: 'docker', label: 'Docker', demand: 'High', availability: 58 },
        { value: 'kubernetes', label: 'Kubernetes', demand: 'High', availability: 42 },
        { value: 'jenkins', label: 'Jenkins', demand: 'Medium', availability: 65 },
        { value: 'terraform', label: 'Terraform', demand: 'Medium', availability: 38 }
      ]
    },
    {
      category: 'Business Functions',
      skills: [
        { value: 'financial-analysis', label: 'Financial Analysis', demand: 'High', availability: 72 },
        { value: 'hr-operations', label: 'HR Operations', demand: 'Medium', availability: 85 },
        { value: 'customer-support', label: 'Customer Support', demand: 'High', availability: 92 },
        { value: 'digital-marketing', label: 'Digital Marketing', demand: 'High', availability: 78 },
        { value: 'content-writing', label: 'Content Writing', demand: 'Medium', availability: 88 }
      ]
    }
  ];

  const locations = [
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'pune', label: 'Pune' },
    { value: 'chennai', label: 'Chennai' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi-ncr', label: 'Delhi NCR' },
    { value: 'kochi', label: 'Kochi' },
    { value: 'ahmedabad', label: 'Ahmedabad' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'lead', label: 'Lead/Architect (10+ years)' }
  ];

  // Flatten skills for select options
  const allSkills = skillCategories.flatMap(category => 
    category.skills.map(skill => ({
      value: skill.value,
      label: skill.label,
      category: category.category
    }))
  );

  const handleAnalyze = () => {
    if (selectedSkills.length > 0 && location && experienceLevel) {
      setShowResults(true);
      onAnalyze({
        skills: selectedSkills,
        location,
        experienceLevel
      });
    }
  };

  // Mock data for visualization
  const getSkillAvailabilityData = () => {
    return selectedSkills.map(skillValue => {
      const skill = skillCategories
        .flatMap(cat => cat.skills)
        .find(s => s.value === skillValue);
      
      const locationMultiplier = {
        'bangalore': 1.2,
        'hyderabad': 1.1,
        'pune': 1.0,
        'chennai': 0.9,
        'mumbai': 1.15,
        'delhi-ncr': 1.05,
        'kochi': 0.8,
        'ahmedabad': 0.75
      };

      const experienceMultiplier = {
        'entry': 1.3,
        'mid': 1.0,
        'senior': 0.7,
        'lead': 0.4
      };

      const adjustedAvailability = Math.min(100, 
        skill.availability * 
        (locationMultiplier[location] || 1) * 
        (experienceMultiplier[experienceLevel] || 1)
      );

      return {
        skill: skill.label,
        availability: Math.round(adjustedAvailability),
        demand: skill.demand,
        category: skillCategories.find(cat => 
          cat.skills.some(s => s.value === skillValue)
        ).category
      };
    });
  };

  const getRadarData = () => {
    const categories = ['Technical Skills', 'Communication', 'Problem Solving', 'Domain Knowledge', 'Cultural Fit', 'Cost Effectiveness'];
    
    const locationScores = {
      'bangalore': [95, 90, 92, 88, 85, 70],
      'hyderabad': [88, 85, 88, 85, 90, 80],
      'pune': [85, 88, 85, 82, 88, 85],
      'chennai': [82, 92, 85, 85, 85, 88],
      'mumbai': [90, 88, 90, 90, 80, 65],
      'delhi-ncr': [85, 85, 88, 88, 82, 75],
      'kochi': [75, 95, 80, 78, 95, 95],
      'ahmedabad': [78, 90, 82, 80, 92, 98]
    };

    return categories.map((category, index) => ({
      category,
      score: locationScores[location]?.[index] || 80
    }));
  };

  const getSkillScore = (skillValue) => {
    const skill = skillCategories
      .flatMap(cat => cat.skills)
      .find(s => s.value === skillValue);
    
    if (!skill) return 0;

    const locationMultiplier = {
      'bangalore': 1.2,
      'hyderabad': 1.1,
      'pune': 1.0,
      'chennai': 0.9,
      'mumbai': 1.15,
      'delhi-ncr': 1.05,
      'kochi': 0.8,
      'ahmedabad': 0.75
    };

    const experienceMultiplier = {
      'entry': 1.3,
      'mid': 1.0,
      'senior': 0.7,
      'lead': 0.4
    };

    const adjustedAvailability = Math.min(100, 
      skill.availability * 
      (locationMultiplier[location] || 1) * 
      (experienceMultiplier[experienceLevel] || 1)
    );

    return Math.round(adjustedAvailability);
  };

  return (
    <div 
      className="rounded-xl p-6 shadow-executive"
      style={{
        backgroundColor: '#0f172a',
        border: '1px solid #475569',
        color: '#f8fafc'
      }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 gradient-executive rounded-lg flex items-center justify-center">
          <Icon name="Users" size={20} color="white" />
        </div>
        <div>
          <h3 
            className="font-headline font-semibold text-lg"
            style={{ color: '#f8fafc' }}
          >
            Talent Availability Assessment
          </h3>
          <p 
            className="text-sm"
            style={{ color: '#cbd5e1' }}
          >
            Analyze talent pool depth for your specific requirements
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Required Skills"
            placeholder="Select skills"
            options={allSkills}
            value={selectedSkills}
            onChange={setSelectedSkills}
            multiple
            searchable
            description="Choose the skills you need for your GCC"
          />

          <Select
            label="Location"
            placeholder="Select location"
            options={locations}
            value={location}
            onChange={setLocation}
            searchable
            description="Target city for your GCC"
          />

          <Select
            label="Experience Level"
            placeholder="Select experience"
            options={experienceLevels}
            value={experienceLevel}
            onChange={setExperienceLevel}
            description="Required experience level"
          />
        </div>

        {selectedSkills.length > 0 && (
          <div className="space-y-4">
            <h4 
              className="font-medium"
              style={{ color: 'var(--color-text-primary)' }}
            >
              Talent Availability Analysis
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedSkills.map((skill, index) => {
                const score = getSkillScore(skill);
                return (
                  <div 
                    key={skill} 
                    className="rounded-lg p-4"
                    style={{ 
                      border: '1px solid var(--color-border)',
                      backgroundColor: 'var(--color-surface)'
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h5 
                        className="font-medium"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {skill}
                      </h5>
                      <div className={`px-2 py-1 rounded text-xs ${
                        score >= 80 ? 'bg-success/10 text-success' :
                        score >= 60 ? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                      }`}>
                        {score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low'}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span 
                          className="text-sm"
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          Availability:
                        </span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-500 ${
                                score >= 80 ? 'bg-success' : score >= 60 ? 'bg-warning' : 'bg-error'
                              }`}
                              style={{ width: `${score}%` }}
                            />
                          </div>
                          <span 
                            className="text-sm font-medium"
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {score}%
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--color-text-secondary)' }}>Avg. Salary:</span>
                        <span style={{ color: 'var(--color-text-primary)' }}>
                          ${Math.round(80000 + Math.random() * 40000).toLocaleString()}
                        </span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--color-text-secondary)' }}>Market Demand:</span>
                        <span style={{ color: 'var(--color-text-primary)' }}>
                          {score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div 
          className="rounded-lg p-4"
          style={{ backgroundColor: 'var(--color-muted)' }}
        >
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <div>
              <h4 
                className="font-medium mb-1"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Analysis Factors
              </h4>
              <p 
                className="text-sm"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Our assessment considers current market demand, salary benchmarks, skill availability, competition levels, and regional talent pool depth for accurate planning.
              </p>
            </div>
          </div>
        </div>

        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleAnalyze}
          disabled={selectedSkills.length === 0 || !location || !experienceLevel}
          className="bg-cta hover:bg-cta/90 text-cta-foreground"
          iconName="BarChart3"
          iconPosition="left"
        >
          Analyze Talent Availability
        </Button>
      </div>
    </div>
  );
};

export default TalentAvailability;